import React from "react";
import { useTranslation } from "react-i18next";
import {
  docHasGroups,
  docLeaves,
  formatDocCellForDisplay,
  getDocFieldWidget,
} from "./utils";

const countLeaves = (col) => {
  if (col.kind === "group") {
    return (col.columns || []).reduce((sum, c) => sum + countLeaves(c), 0);
  }
  return 1;
};

export default function DeliveryNoteDetailsDocTable({ schema, documentation }) {
  const { t } = useTranslation();
  const columns = schema?.columns || [];
  const rows = documentation?.rows || [];
  const documentColumns = schema?.document_columns || [];
  const documentValues = documentation?.document_values || {};
  const leaves = docLeaves(columns);
  const hasGroups = docHasGroups(columns);
  const rowLabel = schema?.row_label || t("common.line");

  return (
    <>
      {columns.length > 0 && (
        <div className="table-responsive">
          <table className="table table-delivery_note table-hover">
            <thead>
              {hasGroups ? (
                <>
                  <tr>
                    <th rowSpan={2} width={1}>
                      {rowLabel}
                    </th>
                    {columns.map((col, ci) =>
                      col.kind === "group" ? (
                        <th key={`g-${ci}`} colSpan={countLeaves(col)} className="text-center">
                          {col.title}
                        </th>
                      ) : (
                        <th key={col.id} rowSpan={2}>
                          {col.title}
                          {col["x-unit"] && <small className="text-muted ms-1">({col["x-unit"]})</small>}
                        </th>
                      ),
                    )}
                  </tr>
                  <tr>
                    {columns.flatMap((col) =>
                      col.kind === "group"
                        ? docLeaves([col]).map((leaf) => (
                            <th key={leaf.id}>
                              {leaf.title}
                              {leaf["x-unit"] && <small className="text-muted ms-1">({leaf["x-unit"]})</small>}
                            </th>
                          ))
                        : [],
                    )}
                  </tr>
                </>
              ) : (
                <tr>
                  <th width={1}>{rowLabel}</th>
                  {leaves.map((leaf) => (
                    <th key={leaf.id}>
                      {leaf.title}
                      {leaf["x-unit"] && <small className="text-muted ms-1">({leaf["x-unit"]})</small>}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((row, rowi) => (
                <tr key={`doc-row-${rowi}`}>
                  <td>{rowi + 1}</td>
                  {leaves.map((leaf) => {
                    const isBoolean = getDocFieldWidget(leaf) === "boolean";
                    return (
                      <td key={leaf.id} className={isBoolean ? "text-end" : ""}>
                        {formatDocCellForDisplay(leaf, row[leaf.id])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {documentColumns.length > 0 && (
        <ul className="list-unstyled mt-2 mb-0 doc-document-fields">
          {documentColumns.map((col) => {
            const value = documentValues[col.id];
            const label = col.description || col.title;
            if (getDocFieldWidget(col) === "boolean") {
              return (
                <li key={col.id} className="d-flex align-items-start gap-2 py-1">
                  <span className="mt-1" aria-hidden="true">
                    {value ? "☑" : "☐"}
                  </span>
                  <span>{label}</span>
                </li>
              );
            }
            return (
              <li key={col.id} className="py-1">
                {col.title && <strong className="me-2">{col.title}:</strong>}
                <span>{formatDocCellForDisplay(col, value)}</span>
                {col.description && <em className="text-muted ms-2">{col.description}</em>}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
